import { LinkRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Controller, useForm, useWatch } from "react-hook-form";
import { IUser } from "src/models";
import { v4 as newGuid } from "uuid";
import { Autocomplete, AutocompleteOption, SmallInputLabel, SnackbarContent, TextEditor, UserAutocomplete } from "..";
import { Links } from "../../constants";
import { ActionItemTeamPlanSectionDto, useCreateActionItem, useGetUsers } from "../../http";

interface ICreateActionItemFormState {
  description: string;
  notes: string;
  owners: IUser[];
  primaryOwner: IUser | null;
}

interface IProps {
  visible: boolean;
  teamPlanSection: ActionItemTeamPlanSectionDto;
  context?: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  onCreated: () => void;
  onCancelled: () => void;
}

const CreateActionItemModal = (props: IProps) => {
  const { isLoading: isLoadingUsers, data: users } = useGetUsers(props.visible);
  const { mutate: createActionItem, isLoading: isCreatingActionItem } = useCreateActionItem();
  const { enqueueSnackbar } = useSnackbar();

  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
  } = useForm<ICreateActionItemFormState>({
    defaultValues: {
      description: "",
      notes: "",
      owners: [],
      primaryOwner: null,
    },
  });

  const owners = useWatch({ control, name: "owners" });

  async function handleAddClicked() {
    const newActionItemGuid = newGuid();

    handleSubmit((formState) => {
      createActionItem(
        {
          teamPlanSection: props.teamPlanSection,
          actionItemGuid: newActionItemGuid,
          contextActionItemGuid: props.context?.actionItemGuid,
          description: formState.description,
          notes: formState.notes,
          owners: formState.owners,
          primaryOwner: formState.primaryOwner,
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            enqueueSnackbar(
              <SnackbarContent
                title="Item Created"
                content={
                  <>
                    Item added to{" "}
                    <Link href={Links.TeamStrategicMap(props.teamPlanSection.teamSlug)}>
                      {props.teamPlanSection.teamName}
                    </Link>
                  </>
                }
              />,
              {
                variant: "success",
                action: <Button href={Links.ActionItemDetail(newActionItemGuid)}>View Item</Button>,
              },
            );

            props.onCreated();
          },
        },
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Add Strategic Item</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {props.context && (
            <Stack>
              <SmallInputLabel>Context</SmallInputLabel>
              <Stack direction="row" spacing={0.25} sx={{ alignItems: "start" }}>
                <LinkRounded sx={{ position: "relative", top: 4 }} />
                <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  <Box component="span" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    {props.context?.teamName}
                  </Box>
                  {` : ${props.context?.actionItemDescription}`}
                </Typography>
              </Stack>
            </Stack>
          )}

          <Stack spacing={0.25}>
            <SmallInputLabel>Description *</SmallInputLabel>
            <Controller
              name="description"
              control={control}
              rules={{ required: { message: "Description is required", value: true } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors["description"] !== undefined}
                  helperText={errors["description"]?.message}
                  placeholder="Enter item..."
                  autoFocus
                  multiline
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25}>
            <SmallInputLabel>Notes</SmallInputLabel>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => <TextEditor initialValue={field.value} onChange={field.onChange} />}
            />
          </Stack>
          <Stack spacing={0.25}>
            <SmallInputLabel>Ownership</SmallInputLabel>
            <Controller
              name="owners"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isLoadingUsers}
                  disabled={isLoadingUsers}
                  options={users ?? []}
                  getOptionLabel={(option) => option.displayName}
                  isOptionEqualToValue={(option, value) => option.userId === value.userId}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={option.userId}>
                      <Stack>
                        <Typography>{option.displayName}</Typography>
                        <Typography variant="caption">{option.email}</Typography>
                      </Stack>
                    </AutocompleteOption>
                  )}
                  onChange={(_, option) => field.onChange(option)}
                  renderInput={(params) => <TextField {...params} placeholder="Select people..." />}
                  value={field.value}
                  multiple
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25}>
            <SmallInputLabel disabled={owners.length <= 1}>Primary Owner</SmallInputLabel>
            <Controller
              name="primaryOwner"
              control={control}
              render={({ field }) => (
                <UserAutocomplete
                  options={owners}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select a primary owner..."
                  disabled={owners.length <= 1}
                />
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isCreatingActionItem} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isCreatingActionItem}
          endIcon={isCreatingActionItem ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleAddClicked}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateActionItemModal };
